import { baseApi } from './baseApi'
import type {
	InvoiceData,
	InvoiceFormValues,
	List, Single,
} from '../../types'
import { InvoiceStatus } from '../../types'
import { invoicesPerPage } from '../../constants'

interface GetInvoicesRequest {
	page?: number
	per_page?: number
	filters?: {
		status?: InvoiceStatus[]
	}
	order_by?: string
}

interface PostInvoiceRequest {
	data: InvoiceFormValues
	id: number
}

interface UploadFileRequest {
	file: File
}

export const invoicesApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		getInvoices: builder.query<List<InvoiceData>, GetInvoicesRequest | void>({
			query: (params) => ({
				url: '/invoices',
				method: 'POST',
				body: {
					per_page: params?.per_page || invoicesPerPage,
					filters: params?.filters,
					...(params?.order_by && { order_by: params.order_by }),
				},
				params: params?.page ? { page: params.page } : undefined,
			}),
			providesTags: ['Invoice'],
		}),

		getIncomingInvoices: builder.query<List<InvoiceData>, number>({
			query: (page) => ({
				url: '/invoices',
				method: 'POST',
				params: { page },
				body: {
					per_page: invoicesPerPage,
					filters: {
						status: [InvoiceStatus.ready_to_review],
					},
				},
			}),
			providesTags: ['Invoice'],
		}),

		getPendingInvoices: builder.query<List<InvoiceData>, number>({
			query: (page) => ({
				url: '/invoices',
				method: 'POST',
				params: { page },
				body: {
					per_page: invoicesPerPage,
					filters: {
						status: [InvoiceStatus.failed],
					},
				},
			}),
			providesTags: ['Invoice'],
		}),

		getProcessedInvoices: builder.query<List<InvoiceData>, number>({
			query: (page) => ({
				url: '/invoices',
				method: 'POST',
				params: { page, order_by: 'reviewed_at' },
				body: {
					per_page: invoicesPerPage,
					filters: {
						status: [InvoiceStatus.reviewed],
					},
				},
			}),
			providesTags: ['Invoice'],
		}),

		postInvoice: builder.mutation<Single<InvoiceData>, PostInvoiceRequest>({
			query: ({ data, id }) => ({
				url: `/invoices/${id}/review`,
				method: 'PATCH',
				body: data,
			}),
			invalidatesTags: ['Invoice'],
		}),

		uploadInvoiceFile: builder.mutation<
			{ message?: string; data?: InvoiceData },
			UploadFileRequest
		>({
			query: ({ file }) => {
				const formData = new FormData()
				formData.append('file', file)
				return {
					url: '/upload-file',
					method: 'POST',
					body: formData,
				}
			},
			invalidatesTags: ['Invoice'],
		}),

		deleteInvoice: builder.mutation<{ message?: string }, number>({
			query: (id) => ({
				url: `/invoices/${id}/delete`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Invoice'],
		}),
	}),
})

export const {
	useGetInvoicesQuery,
	useGetIncomingInvoicesQuery,
	useGetPendingInvoicesQuery,
	useGetProcessedInvoicesQuery,
	usePostInvoiceMutation,
	useUploadInvoiceFileMutation,
	useDeleteInvoiceMutation,
} = invoicesApi

